<template>
  <Card class="w-full">
    <!-- 헤더: 제목, Badge 목록, 초기화 버튼 -->
    <CardHeader class="pt-2 pb-2 px-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 flex-1">
          <button
            v-if="collapsible"
            type="button"
            class="flex items-center gap-2 font-semibold hover:text-primary transition-colors"
            @click="isExpanded = !isExpanded"
          >
            <ChevronDown
              :class="['h-4 w-4 transition-transform', isExpanded ? 'rotate-0' : '-rotate-90']"
            />
            {{ title }}
          </button>
          <CardTitle v-else class="mb-0">{{ title }}</CardTitle>
          
          <!-- 조건 Badge 목록 -->
          <div v-if="conditionBadges.length > 0" class="flex flex-wrap items-center gap-1 ml-1">
            <JBadge
              v-for="badge in conditionBadges"
              :key="badge.fieldName"
              variant="secondary"
              size="sm"
              class="flex items-center gap-1.5 pr-1"
            >
              <span>{{ badge.label }}: {{ badge.value }}</span>
              <button
                type="button"
                class="h-4 w-4 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors flex items-center justify-center"
                :aria-label="`${badge.label} 조건 제거`"
                @click.stop="handleFieldReset(badge.fieldName)"
              >
                <X class="h-3 w-3" />
              </button>
            </JBadge>
          </div>
        </div>
        
        <JButton
          variant="outline"
          size="sm"
          @click.stop="handleReset"
        >
          초기화
        </JButton>
      </div>
    </CardHeader>
    
    <!-- 폼 내용 -->
    <CardContent v-show="isExpanded || !collapsible" class="px-3 pb-2 pt-0">
      <JDynamicForm
        ref="dynamicFormRef"
        :schema="schema"
        :model-value="localModelValue"
        @update:model-value="handleFormValueUpdate"
        @submit="handleSubmit"
      />
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch } from 'vue'
import { ChevronDown, X } from 'lucide-vue-next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/shadcn'
import { JButton, JBadge } from '@/components/atoms'
import JDynamicForm from './JDynamicForm.vue'
import type { FormSchema, DynamicFormField } from '@/types/dynamic-form'
import type { ComponentPublicInstance } from 'vue'

interface ConditionBadge {
  fieldName: string
  label: string
  value: string
}

export interface JSearchPanelProps {
  /** 패널 제목 */
  title?: string
  /** JDynamicForm에 전달할 FormSchema */
  schema: FormSchema
  /** JDynamicForm의 v-model 값 */
  modelValue?: Record<string, any>
  /** 기본 접힘 상태 */
  defaultCollapsed?: boolean
  /** 접기/펼치기 가능 여부 */
  collapsible?: boolean
}

const props = withDefaults(defineProps<JSearchPanelProps>(), {
  title: '조회조건',
  defaultCollapsed: false,
  collapsible: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, any>]
  'submit': [values: Record<string, any>]
  'reset': []
}>()

const dynamicFormRef = ref<ComponentPublicInstance & { reset: () => void } | null>(null)
const isExpanded = ref(!props.defaultCollapsed)

// 로컬 modelValue - reactive로 관리하여 양방향 바인딩
const localModelValue = reactive<Record<string, any>>(props.modelValue ? { ...props.modelValue } : {})

// 내부 변경인지 외부 변경인지 구분하는 플래그
let isInternalUpdate = false

// props.modelValue 변경 시 localModelValue 동기화 (외부 변경)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && !isInternalUpdate) {
      // 기존 키 중 새 값에 없는 것은 삭제
      Object.keys(localModelValue).forEach(key => {
        if (!(key in newValue)) {
          delete localModelValue[key]
        }
      })
      // 새 값으로 업데이트
      Object.assign(localModelValue, newValue)
    }
    isInternalUpdate = false
  },
  { deep: true, immediate: true }
)

// JDynamicForm에서 값이 변경되었을 때 처리
function handleFormValueUpdate(value: Record<string, any>) {
  // JDynamicForm에서 emit된 값을 localModelValue에 반영
  isInternalUpdate = true
  Object.assign(localModelValue, value)
  // 상위로 emit
  emit('update:modelValue', { ...value })
}

// 모든 필드 가져오기
const allFields = computed((): DynamicFormField[] => {
  if (!props.schema) return []
  
  const fields: DynamicFormField[] = []
  if (props.schema.type === 'simple' && props.schema.fields) {
    fields.push(...props.schema.fields)
  } else if (props.schema.type === 'sectioned' && props.schema.sections) {
    props.schema.sections.forEach(section => {
      if (section.fields) fields.push(...section.fields)
    })
  } else if (props.schema.type === 'wizard' && props.schema.steps) {
    props.schema.steps.forEach(step => {
      if (step.fields) fields.push(...step.fields)
    })
  }
  return fields
})

// 조건 Badge 목록 생성
const conditionBadges = computed((): ConditionBadge[] => {
  if (!localModelValue || !props.schema) {
    return []
  }

  const badges: ConditionBadge[] = []
  const formState = localModelValue

  allFields.value.forEach((field) => {
    const value = formState[field.controlName]
    
    // 빈 값 체크 (빈 문자열, null, undefined, 'ALL', 'SELECT' 제외)
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === 'ALL' ||
      value === 'SELECT' ||
      (field.type === 'checkbox' && value === 'N') ||
      (field.type === 'switch' && value === 'N')
    ) {
      return
    }

    // 필드 타입에 따라 값 표시
    let displayValue = String(value)

    // 콤보/검색콤보인 경우 옵션의 label 찾기
    if ((field.type === 'combo' || field.type === 'searchcombo') && field.options) {
      const option = field.options.find(opt => opt.value === value)
      if (option) {
        displayValue = option.label
      }
    }

    // 체크박스/스위치는 'Y'일 때만 표시
    if (field.type === 'checkbox' || field.type === 'switch') {
      if (value === 'Y') {
        badges.push({
          fieldName: field.controlName,
          label: field.label,
          value: field.inlineLabel || field.label
        })
      }
    } else {
      badges.push({
        fieldName: field.controlName,
        label: field.label,
        value: displayValue
      })
    }
  })

  return badges
})

// submit 핸들러
function handleSubmit(values: Record<string, any>) {
  emit('submit', values)
}

// 전체 초기화 핸들러
function handleReset() {
  if (dynamicFormRef.value) {
    dynamicFormRef.value.reset()
    emit('reset')
  }
}

// 필드별 초기화 핸들러
function handleFieldReset(fieldName: string) {
  if (!localModelValue || !props.schema) return
  
  const field = allFields.value.find(f => f.controlName === fieldName)
  
  if (field) {
    // 필드 타입에 따라 초기값 설정 - reactive 객체 직접 수정
    if (field.type === 'checkbox' || field.type === 'switch') {
      localModelValue[fieldName] = 'N'
    } else {
      localModelValue[fieldName] = ''
    }
    
    // localModelValue 변경 후 상위로 emit하여 JDynamicForm에도 반영
    isInternalUpdate = true
    emit('update:modelValue', { ...localModelValue })
  }
}
</script>

<style scoped>
/* 필요시 스타일 추가 */
</style>
